import { baseApi } from './baseApi'
import {
  CompaniesListQuery,
  CompanyDto,
  CreateCompanyDto,
  CreateOfficeDto,
  Offices,
  OutputOffice,
  UpdateOfficeDto,
} from 'store/types'
import { OfficeSettingsDto } from 'ts-back-end/src/business/office/dto/office.dto'
import { PhysicianFollowUpPhysicianDto } from 'ts-back-end/src/business/physician/dto/physician.dto'

export const officeApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getCompanies: builder.query<CompanyDto, { name?: string; limit?: number }>({
      query: (params) => ({ url: `/lab/companies`, params }),

      providesTags: ['Company'],
    }),
    getCompanyList: builder.query<CompanyDto[], CompaniesListQuery>({
      query: (params) => ({ url: `/lab/companies/list`, params }),
      providesTags: ['Company'],
    }),
    getCompany: builder.query<CompanyDto, { id: number }>({
      query: ({ id }) => ({ url: `/lab/companies/${id}` }),
      providesTags: (result, error, { id }) => [{ type: 'Company', id }],
    }),
    getDmeCompany: builder.query<CompanyDto, void>({
      query: () => ({ url: `/dme/company` }),
      providesTags: ['Company'],
    }),
    createCompany: builder.mutation<CompanyDto, CreateCompanyDto>({
      query: (data) => ({
        method: `POST`,
        url: `/lab/companies`,
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Company', id }],
    }),
    updateCompany: builder.mutation<CompanyDto, Partial<CompanyDto>>({
      query: (data) => ({
        method: `PUT`,
        url: `/lab/companies/${data.id}`,
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Company', id }],
    }),
    addCompanyRegion: builder.mutation<
      void,
      {
        id: number
        regionName: string
        parentUuid?: string | null
        managerKeyPersonIndex?: number | null
      }
    >({
      query: ({ id, regionName, parentUuid, managerKeyPersonIndex }) => ({
        method: `POST`,
        url: `/lab/companies/${id}/regions`,
        body: { regionName, parentUuid, managerKeyPersonIndex },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Company', id }],
    }),
    updateCompanyRegion: builder.mutation<
      void,
      {
        id: number
        data: {
          uuid: string
          regionName?: string
          parentUuid?: string | null
          managerKeyPersonIndex?: number | null
        }
      }
    >({
      query: ({ id, data }) => ({
        method: `PUT`,
        url: `/lab/companies/${id}/regions/${data.uuid}`,
        body: {
          regionName: data.regionName,
          parentUuid: data.parentUuid,
          managerKeyPersonIndex: data.managerKeyPersonIndex,
        },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Company', id }],
    }),
    deleteCompanyRegion: builder.mutation<void, { id: number; uuid: string }>({
      query: ({ id, uuid }) => ({
        method: `DELETE`,
        url: `/lab/companies/${id}/regions/${uuid}`,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Company', id }],
    }),
    getOfficeSettings: builder.query<OfficeSettingsDto, { id: number }>({
      query: ({ id }) => ({ url: `/lab/offices/${id}/settings` }),
      providesTags: (result, error, { id }) => [{ type: 'Office', id: `settings-${id}` }],
    }),
    updateOfficeSettings: builder.mutation<OfficeSettingsDto, { id: number } & OfficeSettingsDto>({
      query: ({ id, ...data }) => ({
        method: `PUT`,
        url: `/lab/offices/${id}/settings`,
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Office', id: `settings-${id}` }],
    }),
    getOfficeFollowUpPhysicians: builder.query<PhysicianFollowUpPhysicianDto[], { id: number }>({
      query: ({ id }) => ({ url: `/lab/offices/${id}/follow-up-physicians` }),
      providesTags: (result, error, { id }) => [{ type: 'FollowUpProviders', id: `office-${id}` }],
    }),
    addOfficeFollowUpPhysician: builder.mutation<void, { id: number; followUpPhysicianNpi: string }>({
      query: ({ id, followUpPhysicianNpi }) => ({
        method: `POST`,
        url: `/lab/offices/${id}/follow-up-physicians`,
        body: { followUpPhysicianNpi },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'FollowUpProviders', id: `office-${id}` }],
    }),
    removeOfficeFollowUpPhysician: builder.mutation<{ npi: string }, { id: number; npi: string }>({
      query: ({ id, npi }) => ({
        method: `DELETE`,
        url: `/lab/offices/${id}/follow-up-physicians/${npi}`,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'FollowUpProviders', id: `office-${id}` }],
    }),
    getLabOffices: builder.query<Offices, { name?: string; npi?: string; companyId?: number; includeDeleted?: boolean }>({
      query: (params) => ({ url: `/lab/offices`, params }),
      providesTags: ['Office'],
    }),
    getLabOfficeList: builder.query<
      Offices,
      { name?: string; npi?: string; companyId?: number; page: number }
    >({
      query: (params) => ({ url: `/lab/offices/list`, params }),
      providesTags: ['OfficeList'],
    }),
    getLabOffice: builder.query<OutputOffice, { id: number }>({
      query: ({ id }) => ({ url: `/lab/offices/${id}` }),
      providesTags: (result, error, { id }) => [{ type: 'Office', id }],
    }),
    createOffice: builder.mutation<OutputOffice, CreateOfficeDto>({
      query: (data) => ({
        method: `POST`,
        url: `/lab/offices`,
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Office', id }, { type: 'OfficeList' }],
    }),
    updateOffice: builder.mutation<OutputOffice, UpdateOfficeDto>({
      query: (data) => ({
        method: `PUT`,
        url: `/lab/offices/${data.id}`,
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Office', id }, { type: 'OfficeList' }],
    }),
    mergeOffice: builder.mutation<void, { sourceOfficeId: number; targetOfficeId: number }>({
      query: ({ sourceOfficeId, targetOfficeId }) => ({
        method: `POST`,
        url: `/lab/offices/${sourceOfficeId}/merge`,
        body: { targetOfficeId },
      }),
      invalidatesTags: (result, error, { sourceOfficeId, targetOfficeId }) => [
        { type: 'Office', id: sourceOfficeId },
        { type: 'Office', id: targetOfficeId },
        { type: 'OfficeList' },
      ],
    }),
    getOffices: builder.query<Offices, { company_id: number }>({
      query: ({ company_id }) => ({ url: `/dme/office`, params: { company_id } }),
    }),
    getDmeOffices: builder.query<Offices, void>({
      query: () => ({ url: `/dme/offices` }),
    }),
    getOfficeDocument: builder.query<string, { officeId: number; documentKey: string }>({
      query: ({ officeId, documentKey }) => ({
        url: `/dme/office/${officeId}/documents`,
        params: { documentKey },
      }),
    }),
    getDmeOfficeTests: builder.query<OutputOffice, { id: number }>({
      query: ({ id }) => ({ url: `/dme/offices/${id}/test-types` }),
      providesTags: (result, error, { id }) => [{ type: 'Office', id }],
    }),
    updateOfficeAvailableTests: builder.mutation<
      void,
      { id: number; availableTests: { POX: boolean; COX: boolean; HST: boolean; PSG: boolean } }
    >({
      query: ({ id, availableTests }) => ({
        method: `PUT`,
        url: `/lab/offices/${id}`,
        body: {
          id,
          availableTests,
        },
      }),
      invalidatesTags: (result, error, { id }) => [{ type: 'Office', id }],
    }),
    deleteOffice: builder.mutation<void, { id: number }>({
      query: ({ id }) => ({
        method: `DELETE`,
        url: `/lab/offices/${id}`,
      }),
      invalidatesTags: ['OfficeList'],
    }),
  }),
})

export const {
  useGetCompaniesQuery,
  useGetCompanyListQuery,
  useGetCompanyQuery,
  useCreateCompanyMutation,
  useGetOfficeSettingsQuery,
  useUpdateOfficeSettingsMutation,
  useGetOfficeFollowUpPhysiciansQuery,
  useAddOfficeFollowUpPhysicianMutation,
  useRemoveOfficeFollowUpPhysicianMutation,
  useAddCompanyRegionMutation,
  useUpdateCompanyRegionMutation,
  useDeleteCompanyRegionMutation,
  useGetOfficesQuery,
  useGetLabOfficesQuery,
  useGetLabOfficeListQuery,
  useGetLabOfficeQuery,
  useGetOfficeDocumentQuery,
  useCreateOfficeMutation,
  useMergeOfficeMutation,
  useUpdateOfficeMutation,
  useUpdateCompanyMutation,
  useUpdateOfficeAvailableTestsMutation,
  useDeleteOfficeMutation,
  useGetDmeOfficeTestsQuery,
  useLazyGetDmeOfficeTestsQuery,
  useGetDmeOfficesQuery,
  useGetDmeCompanyQuery,
} = officeApi
